/**
 * @file lineAccountRequired.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description A typed line names its own accounts (Cam, 3 Nov 2026 — "Typed
 *              lines should require user to input sales and purchase account
 *              codes when orders are created (and invoices or whatever)").
 *
 *   WHY. Four October invoice lines went to Xero on the default sales account
 *   200 because they were typed lines — no inventory item, so nothing to take
 *   an account from: INV-D00262's "Buta-cup Extra" and INV-D00037's "Next day
 *   Del" came from customer order lines with no item, and invoicing from an
 *   order fell back to 200 (services/orderInvoiceService.invoiceLinesFor).
 *
 *   A TYPED LINE is one that names something (words or a code) but no
 *   inventory item. An item line takes its accounts from its item and is asked
 *   nothing here.
 *
 *   WHERE AND WHAT
 *     - A customer order line (Create / Edit Customer Order; POST / PUT
 *       /api/orders/customer-orders): its own SALES account always
 *       (`line_sales_account_code` — what its invoice line is booked to), and
 *       its own PURCHASE account when it will be bought on a PO — a supplier is
 *       chosen and it is not one of our stock suppliers
 *       (`line_purchase_account_code` — what its PO line, and so its bill, is
 *       booked to).
 *     - An invoice or credit note line (Create Invoice, Add credit / debit;
 *       POST / PUT /api/invoices): its account (`account_code`).
 *     - A purchase order line (Create / Edit PO; POST / PUT
 *       /api/purchase-orders): its account (`accountCode`) — except a line
 *       bought for an order line, whose account is its order line's (set on the
 *       order; the PO save copies it across).
 *   Refused 400 ACCOUNT_REQUIRED naming the lines; nothing saved.
 *
 *   WHICH LINES (as units — utils/lineUnitRequired): a new line, and a line
 *   already saved whose item, words, quantity or price this save changes, or
 *   whose account it clears. An old line left alone still saves, so a document
 *   saved before the rule is not stuck.
 *
 *   The server's utils/lineAccountRequired.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

/** The save's refusal code. */
export const ACCOUNT_REQUIRED_CODE = 'ACCOUNT_REQUIRED';

/** A customer order line's own accounts (orders.order_lines columns and API fields). */
export const LINE_SALES_ACCOUNT_FIELD = 'line_sales_account_code';
export const LINE_PURCHASE_ACCOUNT_FIELD = 'line_purchase_account_code';

/** Before migrations/order_line_account_codes.sql: nowhere to keep an order line's accounts. */
export const ORDER_LINE_ACCOUNTS_NOT_READY = 'Not ready — migration order_line_account_codes.sql has not run';

/** The words a missing picker carries. */
export const SALES_ACCOUNT_PROBLEM_TEXT = 'Choose a sales account — this line has no item to take one from (it is what the invoice line is booked to)';
export const PURCHASE_ACCOUNT_PROBLEM_TEXT = 'Choose a purchase account — this line is bought on a PO and has no item to take one from (it is what the PO line and the bill are booked to)';
export const DOCUMENT_ACCOUNT_PROBLEM_TEXT = 'Choose an account — this line has no item to take one from';

const text = (v) => String(v ?? '').trim();
const num = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** An account code as stored and compared: trimmed, upper case ('' = none). */
export function accountCodeOf(v) {
  const s = text(v);
  return s ? s.toUpperCase() : '';
}

// Either shape: a grid line (product_name, product_code, unit_price,
// account_code), an invoice line (description, item_code), a PO JSON line
// (description, itemCode, unitAmount, accountCode).
const wordsOf = (l) => text(l?.product_name ?? l?.description);
const codeOf = (l) => text(l?.product_code ?? l?.item_code ?? l?.itemCode);
const priceOf = (l) => num(l?.unit_price ?? l?.unitAmount);
const qtyOf = (l) => num(l?.quantity);
const itemIdOf = (l) => num(l?.inventory_item_id);
const docAccountOf = (l) => accountCodeOf(l?.account_code ?? l?.accountCode);

/** Does the line name anything at all (an item, words or a code)? An empty row is not a line. */
export function lineInUse(line) {
  return !!line && (itemIdOf(line) !== null || !!wordsOf(line) || !!codeOf(line));
}

/**
 * Is it a typed line — naming something, but no inventory item?
 * @param {object} line
 * @param {(line) => boolean} [hasItem] the host's test (default: an inventory_item_id)
 */
export function isTypedLine(line, hasItem = (l) => itemIdOf(l) !== null) {
  return lineInUse(line) && !hasItem(line);
}

const sameText = (a, b) => text(a) === text(b);
const sameUpper = (a, b) => text(a).toUpperCase() === text(b).toUpperCase();

/**
 * Does this save change an ORDER line for the account rule? A line with no
 * stored copy (new) counts as changed; so does a change of item, code, words
 * (its sale words — the purchase words of a row from before they were kept read
 * NULL, which is no change), quantity, price or supplier, or an account it had
 * being cleared.
 * @param {object} line   the line being saved
 * @param {object|undefined} before the line as stored
 */
export function orderLineChangedForAccounts(line, before) {
  if (!before) return true;
  if (itemIdOf(line) !== itemIdOf(before)) return true;
  if (!sameUpper(line.product_code, before.product_code)) return true;
  if (!sameText(line.product_name, before.product_name)) return true;
  if (qtyOf(line) !== qtyOf(before)) return true;
  if (num(line.unit_price) !== num(before.unit_price)) return true;
  // A supplier chosen or changed: it may now be bought on a PO.
  if (line.supplier_contact_id !== undefined && num(line.supplier_contact_id) !== num(before.supplier_contact_id)) return true;
  for (const f of [LINE_SALES_ACCOUNT_FIELD, LINE_PURCHASE_ACCOUNT_FIELD]) {
    if (accountCodeOf(before[f]) && !accountCodeOf(line[f])) return true;
  }
  return false;
}

/**
 * Which of a typed order line's accounts are missing. An item line, or an
 * empty row, needs none.
 * @param {object} line
 * @param {{ bought?: (line) => boolean, hasItem?: (line) => boolean }} [o]
 *        bought: will it be bought on a PO (a supplier, not stock)?
 * @returns {{ sales: boolean, purchase: boolean }} true = missing and required
 */
export function orderLineAccountsMissing(line, { bought = (l) => !!l?.supplier_contact_id, hasItem } = {}) {
  if (!isTypedLine(line, hasItem)) return { sales: false, purchase: false };
  return {
    sales: !accountCodeOf(line[LINE_SALES_ACCOUNT_FIELD]),
    purchase: !!bought(line) && !accountCodeOf(line[LINE_PURCHASE_ACCOUNT_FIELD])
  };
}

const storedOf = (stored, id) => {
  if (!stored || id === null || id === undefined) return undefined;
  if (typeof stored.get === 'function') return stored.get(id) ?? stored.get(Number(id)) ?? stored.get(String(id));
  return stored[id];
};

const labelOf = (line, index) => {
  const name = [codeOf(line), wordsOf(line)].filter(Boolean).join(' ');
  return `Line ${index + 1}${name ? ` (${name})` : ''}`;
};

/**
 * The order lines a save must refuse for want of an account.
 * @param {Array<object>} lines   the lines being saved
 * @param {Map|object|null} stored the order's lines as stored (an edit), by id; null for a new order
 * @param {{ bought?, hasItem? }} [o] as orderLineAccountsMissing
 * @returns {Array<{ index, id, label, missing: Array<'sales'|'purchase'>, text }>}
 */
export function orderLineAccountRefusals(lines, stored = null, o = {}) {
  const out = [];
  (Array.isArray(lines) ? lines : []).forEach((line, index) => {
    if (!line) return;
    if (!orderLineChangedForAccounts(line, storedOf(stored, line.id))) return;
    const m = orderLineAccountsMissing(line, o);
    const missing = [m.sales ? 'sales' : null, m.purchase ? 'purchase' : null].filter(Boolean);
    if (!missing.length) return;
    const label = labelOf(line, index);
    const what = missing.length === 2 ? 'a sales account and a purchase account' : `a ${missing[0]} account`;
    out.push({ index, id: line.id ?? null, label, missing, text: `${label} needs ${what}` });
  });
  return out;
}

/**
 * Does this save change an invoice or PO line for the account rule? New (no
 * stored copy), or its item code, words, quantity or price moved, or its
 * account was cleared.
 */
export function documentLineChangedForAccounts(line, before) {
  if (!before) return true;
  if (!sameUpper(codeOf(line), codeOf(before))) return true;
  if (!sameText(wordsOf(line), wordsOf(before))) return true;
  if (qtyOf(line) !== qtyOf(before)) return true;
  if (priceOf(line) !== priceOf(before)) return true;
  if (docAccountOf(before) && !docAccountOf(line)) return true;
  return false;
}

/**
 * The invoice / PO lines a save must refuse: typed, new or changed, no account.
 * @param {Array<object>} lines
 * @param {{ before?: (line, index) => object|undefined, hasItem?: (line) => boolean,
 *           exempt?: (line) => boolean }} [o]
 *        before: the line as stored or loaded (undefined = new);
 *        exempt: a line whose account is set elsewhere (a PO line bought for an order line)
 * @returns {Array<{ index, id, label, missing: ['account'], text }>}
 */
export function documentLineAccountRefusals(lines, { before = () => undefined, hasItem, exempt = () => false } = {}) {
  const out = [];
  (Array.isArray(lines) ? lines : []).forEach((line, index) => {
    if (!line || exempt(line)) return;
    if (!isTypedLine(line, hasItem)) return;
    if (docAccountOf(line)) return;
    if (!documentLineChangedForAccounts(line, before(line, index))) return;
    const label = labelOf(line, index);
    out.push({ index, id: line.id ?? line.invoice_line_id ?? line.lineItemID ?? null, label, missing: ['account'], text: `${label} needs an account` });
  });
  return out;
}

const WHERE_WORDS = {
  order: 'a line with no inventory item has nothing to take its accounts from, so choose them under its item (Sales account; Purchase account when it is bought on a PO).',
  invoice: 'a line with no inventory item has nothing to take an account from, so choose one in its Account column.',
  po: 'a line with no inventory item has nothing to take an account from, so choose its Purchase account under its item.'
};

/**
 * One sentence for a refusal, or '' when there is none.
 * @param {Array<{ text }>} refusals
 * @param {'order'|'invoice'|'po'} [where] where the accounts are chosen
 */
export function accountRefusalMessage(refusals, where = 'order') {
  if (!refusals || !refusals.length) return '';
  const list = refusals.map(r => r.text);
  const joined = list.length === 1 ? list[0] : `${list.slice(0, -1).join('; ')} and ${list[list.length - 1]}`;
  return `${joined} — ${WHERE_WORDS[where] || WHERE_WORDS.invoice}`;
}

/**
 * The sales account an invoice line from an order line is booked to: the
 * line's own for a typed line, else its item's; '' when neither has one.
 * @param {object} line { inventory_item_id, line_sales_account_code, item_sales_account_code }
 */
export function invoiceSalesAccountFor(line) {
  const own = accountCodeOf(line?.[LINE_SALES_ACCOUNT_FIELD]);
  const item = accountCodeOf(line?.item_sales_account_code);
  return itemIdOf(line) !== null ? (item || own) : (own || item);
}
